import { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { callFunction } from "../api/callFunction";
import { toast } from "react-toastify";
import { db } from "../firebase";
import { GlobalContext } from "../context/GlobalContext";
import {
  toMillis,
  formatRelativeTime,
  formatTimestamp,
} from "../components/MessageThread";

// issueReports: member-submitted bug / platform / app-suggestion reports filed through
// the "Ask Gist AI" assistant. Member-SAFETY reports live in Arbitration, and event-
// EXPERIENCE feedback lives in Feedback (eventFeedback) — not here. Mirrors the
// Arbitration view: live list + detail pane, search/filter, callable mutation.

const STATUS_META = {
  open: { label: "Open", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  in_progress: { label: "In progress", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  resolved: { label: "Resolved", cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" },
};

const CATEGORY_META = {
  bug: { label: "Bug", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  platform: { label: "Platform", cls: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" },
  suggestion: { label: "Suggestion", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
};

function Badge({ cls, children }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
      {children}
    </span>
  );
}

export default function Issues() {
  const { globalState } = useContext(GlobalContext);
  const isSuperAdmin = globalState.isSuperAdmin;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [openOnly, setOpenOnly] = useState(false);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  // Live list of all issue reports (superAdmin-only read, gated by rules).
  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(collection(db, "issueReports"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, _ms: toMillis(data.createdAt) };
        });
        setReports(rows);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading issue reports:", err);
        setLoadError(err?.message || "Failed to load reports.");
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [isSuperAdmin]);

  const selectedReport = useMemo(
    () => reports.find((r) => r.id === selectedReportId) || null,
    [reports, selectedReportId]
  );

  const openCount = useMemo(
    () => reports.filter((r) => r.status !== "resolved").length,
    [reports]
  );

  const filteredReports = useMemo(() => {
    let list = reports;
    if (openOnly) list = list.filter((r) => r.status !== "resolved");
    const ql = searchQuery.trim().toLowerCase();
    if (!ql) return list;
    return list.filter(
      (r) =>
        (r.title && r.title.toLowerCase().includes(ql)) ||
        (r.description && r.description.toLowerCase().includes(ql)) ||
        (r.category && r.category.toLowerCase().includes(ql)) ||
        (r.reporterName && r.reporterName.toLowerCase().includes(ql))
    );
  }, [reports, searchQuery, openOnly]);

  const callUpdate = async (patch) => {
    if (!selectedReportId) return;
    setSaving(true);
    try {
      await callFunction("adminUpdateIssueReport", { reportId: selectedReportId, ...patch }, { timeout: 60000 });
    } catch (err) {
      console.error("adminUpdateIssueReport failed:", err);
      toast.error(err?.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const submitNote = () => {
    const text = noteDraft.trim();
    if (!text) return;
    callUpdate({ addNote: text });
    setNoteDraft("");
  };

  const callDelete = async () => {
    if (!selectedReportId) return;
    if (
      !window.confirm(
        `Delete the report "${selectedReport?.title || "(no title)"}"? This permanently removes it and cannot be undone.`
      )
    )
      return;
    setDeleting(true);
    try {
      await callFunction("adminDeleteIssueReport", { reportId: selectedReportId }, { timeout: 60000 });
      toast.success("Report deleted.");
      setSelectedReportId(null);
    } catch (err) {
      console.error("adminDeleteIssueReport failed:", err);
      toast.error(err?.message || "Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  const openUser = (userID) => {
    if (!userID) return;
    navigate(`/users?q=${encodeURIComponent(userID)}`);
  };

  if (!isSuperAdmin) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 mt-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Issues</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          This view is only available to super admins.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mt-4 sm:mt-0">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Issues</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Bug, platform, and app-suggestion reports members file through Ask Gist AI.
          {reports.length > 0 && <span> {reports.length} total.</span>}
        </p>
      </div>

      <div className="mt-4 flex flex-col lg:flex-row gap-4 h-[calc(100vh-180px)]">
        {/* Report list */}
        <div className="lg:w-80 flex-shrink-0 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search title, category, reporter…"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
            />
            <button
              onClick={() => setOpenOnly((v) => !v)}
              className={`w-full px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                openOnly
                  ? "bg-amber-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              {openOnly ? "Showing unresolved" : "Unresolved only"}
              {openCount > 0 && ` (${openCount})`}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : loadError ? (
              <div className="p-4 text-sm text-red-600 dark:text-red-400">{loadError}</div>
            ) : filteredReports.length === 0 ? (
              <div className="p-4 text-sm text-gray-400 dark:text-gray-500 text-center">No reports found.</div>
            ) : (
              filteredReports.map((r) => {
                const isSelected = r.id === selectedReportId;
                const sm = STATUS_META[r.status] || STATUS_META.open;
                const cm = r.category ? CATEGORY_META[r.category] : null;
                return (
                  <button
                    key={r.id}
                    onClick={() => setSelectedReportId(r.id)}
                    className={`w-full text-left px-3 py-3 border-b border-gray-100 dark:border-gray-800 transition-colors ${
                      isSelected ? "bg-primary/10" : "hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {r.title || "(no title)"}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                        {formatRelativeTime(r._ms)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Badge cls={sm.cls}>{sm.label}</Badge>
                      {cm && <Badge cls={cm.cls}>{cm.label}</Badge>}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Detail pane */}
        <div className="flex-1 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden min-h-0">
          {!selectedReport ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              Select a report to review it.
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                    {selectedReport.title || "(no title)"}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    submitted {formatRelativeTime(selectedReport._ms)}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Badge cls={(STATUS_META[selectedReport.status] || STATUS_META.open).cls}>
                    {(STATUS_META[selectedReport.status] || STATUS_META.open).label}
                  </Badge>
                  {selectedReport.category && CATEGORY_META[selectedReport.category] && (
                    <Badge cls={CATEGORY_META[selectedReport.category].cls}>
                      {CATEGORY_META[selectedReport.category].label}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Report details */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Submitted by</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100">
                    {selectedReport.reporterName || "Unknown member"}
                    {selectedReport.uid && (
                      <button
                        className="ml-2 text-primary hover:underline text-xs font-mono"
                        onClick={() => openUser(selectedReport.uid)}
                        title="Open in Users"
                      >
                        {selectedReport.uid}
                      </button>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Description</p>
                  <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {selectedReport.description || "(none)"}
                  </p>
                </div>
                {selectedReport.context && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Context</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {selectedReport.context}
                    </p>
                  </div>
                )}
              </div>

              {/* Triage controls */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Triage</p>
                <label className="block text-sm">
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Status</span>
                  <select
                    value={selectedReport.status || "open"}
                    disabled={saving}
                    onChange={(e) => callUpdate({ status: e.target.value })}
                    className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </label>

                {/* Admin notes log */}
                {Array.isArray(selectedReport.adminNotes) && selectedReport.adminNotes.length > 0 && (
                  <div className="space-y-1.5">
                    <span className="block text-xs text-gray-500 dark:text-gray-400">Notes</span>
                    {[...selectedReport.adminNotes]
                      .sort((a, b) => (a.at || 0) - (b.at || 0))
                      .map((n, i) => (
                        <div
                          key={i}
                          className="text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 rounded px-2 py-1"
                        >
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {formatTimestamp(n.at)}
                          </span>
                          <p className="whitespace-pre-wrap">{n.text}</p>
                        </div>
                      ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Add an internal note…"
                    className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitNote();
                    }}
                  />
                  <button
                    onClick={submitNote}
                    disabled={saving || !noteDraft.trim()}
                    className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-40"
                  >
                    Add note
                  </button>
                </div>
              </div>

              {/* Danger zone */}
              <div className="rounded-lg border border-red-200 dark:border-red-900/50 p-3">
                <p className="text-sm font-semibold text-red-700 dark:text-red-400">Danger zone</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Permanently delete this report. This cannot be undone.
                </p>
                <button
                  onClick={callDelete}
                  disabled={deleting}
                  className="mt-2 px-3 py-1.5 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                >
                  {deleting ? "Deleting…" : "Delete report"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
